import { HttpUrlEncodingCodec } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CustomFacet,
  CustomFacetList,
  CustomFacetValue,
  CustomProductList,
  FacetValueTreeStructure,
} from '@catalog/const';
import { Tree } from '@catalog/utils';
import { Breadcrumb, Facet, RouterState, RoutingService } from '@spartacus/core';
import { FacetCollapseState, FacetGroupCollapsedState } from '@spartacus/storefront';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { CustomProductFacetService } from './product-facet.service';

@Injectable({
  providedIn: 'root',
})
export class CustomFacetService {
  facetState = new Map<string, BehaviorSubject<FacetCollapseState>>();

  facetList$: Observable<CustomFacetList> = this.productFacetService.facetList$.pipe(
    withLatestFrom(this.routingService.getRouterState()),
    tap(([facetList, routerState]: [CustomFacetList, RouterState]) => {
      facetList.facets.forEach((facet) => this.initialize(facet));
      facetList.activeFacets = facetList.activeFacets
        .filter((facet) => !this.isExcludedActiveFacet(facet, routerState.state.semanticRoute))
        .map((activeFacet: Breadcrumb) => ({
          ...activeFacet,
          facetValueCode: this.queryCodec.decodeValue(activeFacet.facetValueCode),
          facetValueName: this.queryCodec.decodeValue(activeFacet.facetValueName),
        }));
    }),
    map(([facetList, routerState]) => {
      return {
        ...facetList,
        facets: facetList.facets.filter(
          (facet: CustomFacet) => !this.isHiddenFacet(facet, routerState.state.semanticRoute),
        ),
      };
    }),
  );

  private queryCodec = new HttpUrlEncodingCodec();

  constructor(
    private productFacetService: CustomProductFacetService,
    private routingService: RoutingService,
  ) {}

  createTreeStructure(facetValues: CustomFacetValue[]): FacetValueTreeStructure {
    return new Tree(facetValues);
  }

  /**
   * Returns the observed UI state for the facet.
   *
   * The state is initialized using the `initialize` method.
   */
  getState(facet: Facet): Observable<FacetCollapseState> {
    this.initialize(facet);

    return this.facetState.get(facet.name);
  }

  /**
   * Toggles the facet expanded state. If the expanded state becomes false,
   * the visible values will decrease to the top values only.
   *
   * If the optional value argument is provided the expanded state will be set
   * to this value, regardless of the current `expanded` state.
   */
  toggle(facet: Facet, isExpanded: boolean): void {
    const state = this.getStateSnapshot(facet);

    const toggledState = {
      toggled: isExpanded ? FacetGroupCollapsedState.COLLAPSED : FacetGroupCollapsedState.EXPANDED,
    } as FacetCollapseState;

    if (toggledState.toggled === FacetGroupCollapsedState.COLLAPSED) {
      toggledState.maxVisible = state.topVisible;
    }

    this.updateState(facet, toggledState);
  }

  /**
   * Increases the visible values to the maximum values of the facet.
   */
  increaseVisibleValues(facet: Facet): void {
    this.updateState(facet, { maxVisible: facet.values.length });
  }

  /**
   * Decreases the visible values to the topValueCount.
   *
   * The topValueCount defaults to 6, but can be controlled in
   * the backend as well.
   */
  decreaseVisibleValues(facet: Facet): void {
    this.updateState(facet, { maxVisible: facet.topValueCount });
  }

  getLinkParams(query: string): { [key: string]: string } {
    return {
      // to avoid encoding issues with facets that have space (' ') in their name,
      // we replace the decoded '+' back to empty space ' '.
      // For more, see https://github.com/SAP/spartacus/issues/7348
      query: new HttpUrlEncodingCodec().decodeValue(query).replace(/\+/g, ' '),
    };
  }

  /**
   * Returns the UI state for the facet.
   *
   * The state is initialized using the `initialize` method.
   */
  private getStateSnapshot(facet: Facet): FacetCollapseState {
    return (this.getState(facet) as BehaviorSubject<FacetCollapseState>).value;
  }

  /**
   * Updates the state of the facet in the local facet map.
   */
  private updateState(facet: Facet, property: FacetCollapseState): void {
    const state = { ...this.getStateSnapshot(facet), ...property };
    this.facetState.get(facet.name).next(state);
  }

  private hasState(facet: Facet): boolean {
    return this.facetState.has(facet.name);
  }

  private initialize(facet: Facet): void {
    const topFacets = facet.topValueCount > 0 ? facet.topValueCount : facet.values?.length || 0;
    if (!this.hasState(facet)) {
      this.facetState.set(
        facet.name,
        new BehaviorSubject({
          topVisible: topFacets,
          maxVisible: topFacets,
          toggled: facet.expanded ? FacetGroupCollapsedState.EXPANDED : FacetGroupCollapsedState.COLLAPSED,
        } as FacetCollapseState),
      );
    }
  }

  // Скрывается из показа активных фасетов
  private isExcludedActiveFacet(facet: Breadcrumb, semanticRoute?: string): boolean {
    return (
      facet.facetCode === 'category' ||
      facet.facetCode === 'allCategories' ||
      facet.facetCode === 'allCampaigns' ||
      facet.facetCode === 'availableInStores' ||
      (semanticRoute === CustomProductList.SaleProductList && facet.facetCode === 'sale') ||
      (semanticRoute === CustomProductList.BestOffersProductList && facet.facetCode === 'hasDiscount')
    );
  }

  // Скрывается из показа списка фасетов
  private isHiddenFacet(facet: CustomFacet, semanticRoute?: string): boolean {
    return (
      facet.name === 'Доступно в магазинах' ||
      (semanticRoute === CustomProductList.SaleProductList && facet.code === 'sale') ||
      (semanticRoute === CustomProductList.BestOffersProductList && facet.code === 'hasDiscount')
    );
  }
}
